import { useSyncExternalStore } from 'react';

export type Listener = () => void;

export interface Store<T> {
  getState(): T;
  setState(update: Partial<T> | ((current: T) => Partial<T>)): void;
  subscribe(listener: Listener): () => void;
}

/** Enough store for one screenful of mail. No dependency earns its place here. */
export function createStore<T extends object>(initial: T): Store<T> {
  let state = initial;
  const listeners = new Set<Listener>();

  return {
    getState: () => state,
    setState(update) {
      const patch = typeof update === 'function' ? update(state) : update;
      const next = { ...state, ...patch };
      if (
        Object.keys(patch).every((key) => Object.is(state[key as keyof T], patch[key as keyof T]))
      )
        return;
      state = next;
      for (const listener of [...listeners]) listener();
    },
    subscribe(listener) {
      listeners.add(listener);
      return () => listeners.delete(listener);
    },
  };
}

export function useStore<T extends object, S>(store: Store<T>, select: (state: T) => S): S {
  return useSyncExternalStore(
    store.subscribe,
    () => select(store.getState()),
    () => select(store.getState()),
  );
}
